/**
 * Accès protégé pour l'environnement de staging (staging.n-g.fr).
 * Activé uniquement si le hostname correspond à un hôte staging connu
 * et qu'un mot de passe VITE_STAGING_PASSWORD est défini au build.
 */

export const STAGING_ACCESS_STORAGE_KEY = 'ng-staging-access'

/** Hôtes considérés comme environnement de préproduction. */
export const STAGING_HOSTS = ['staging.n-g.fr']

/**
 * @param {string} [hostname]
 * @param {{ forceEnabled?: boolean }} [options]
 */
export function isStagingHost(hostname = '', options = {}) {
  if (options.forceEnabled === true) return true
  const host = String(hostname).toLowerCase().split(':')[0]
  return STAGING_HOSTS.includes(host)
}

/**
 * Protection active dès que l’hôte est staging (ou forçage env).
 * Sans `VITE_STAGING_PASSWORD`, le portail reste bloqué (pas d’accès ouvert).
 * @param {{ hostname?: string, forceEnabled?: boolean }} [options]
 */
export function isStagingProtectionEnabled({
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  forceEnabled = import.meta.env.VITE_STAGING_PROTECTION === 'true',
} = {}) {
  return isStagingHost(hostname, { forceEnabled })
}

/**
 * @param {string} [password]
 */
export function getStagingPassword(password = import.meta.env.VITE_STAGING_PASSWORD) {
  return typeof password === 'string' ? password : ''
}

/**
 * @param {string} [password]
 */
export function isStagingPasswordConfigured(password = import.meta.env.VITE_STAGING_PASSWORD) {
  return getStagingPassword(password).length > 0
}

/**
 * Jeton de session dérivé du mot de passe (invalide l'ancienne session si le MDP change).
 * @param {string} password
 */
export function createAccessToken(password) {
  const input = `ng-staging-v1:${password}`
  let hash = 2166136261
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(16)
}

/**
 * @param {string} [password]
 * @param {Storage | null} [storage]
 */
export function hasValidStagingAccess(
  password = import.meta.env.VITE_STAGING_PASSWORD,
  storage = typeof window !== 'undefined' ? window.sessionStorage : null,
) {
  if (!password || !storage) return false
  try {
    return storage.getItem(STAGING_ACCESS_STORAGE_KEY) === createAccessToken(password)
  } catch {
    return false
  }
}

/**
 * @param {string} attempt
 * @param {string} [expected]
 */
export function verifyStagingPassword(attempt, expected = import.meta.env.VITE_STAGING_PASSWORD) {
  if (typeof expected !== 'string' || expected.length === 0) return false
  return attempt === expected
}

/**
 * @param {string} [password]
 * @param {Storage | null} [storage]
 */
export function grantStagingAccess(
  password = import.meta.env.VITE_STAGING_PASSWORD,
  storage = typeof window !== 'undefined' ? window.sessionStorage : null,
) {
  if (!password || !storage) return false
  try {
    storage.setItem(STAGING_ACCESS_STORAGE_KEY, createAccessToken(password))
    return true
  } catch {
    return false
  }
}

/**
 * @param {Storage | null} [storage]
 */
export function revokeStagingAccess(storage = typeof window !== 'undefined' ? window.sessionStorage : null) {
  if (!storage) return
  try {
    storage.removeItem(STAGING_ACCESS_STORAGE_KEY)
  } catch {
    // ignore quota / private mode
  }
}
